/**
 * WalletConnect pairing UI: desktop QR from MobX `wcPairingUri` (wagmi display_uri), mobile list + deep links.
 */
import { walletConnectionStore as walletStore } from 'mobxStores/WalletConnectionStore'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useMemo, useState, type ButtonHTMLAttributes } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import styled, { useTheme } from 'styled-components'
import { Button, ButtonVariant, Flex, Modal, Text, TextVariant } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import StatusBar from './components/StatusBar'
import {
  openWalletConnectMobileBridge,
  preferWalletConnectMobileBridge,
  WALLETCONNECT_MOBILE_WALLET_OPTIONS
} from 'utils/walletConnectMobileWallets'

const QrBlock = styled(Flex)`
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin: 24px 0;
`

const StyledQrCode = styled(QRCodeSVG)`
  padding: 16px;
  background: white;
  border-radius: 8px;
  height: 256px;
  width: 256px;
  flex-shrink: 0;
`

const StyledQrCodeEmpty = styled(Flex)`
  padding: 16px;
  border-radius: 8px;
  height: 265px;
  width: 256px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const WalletRow = styled.button<ButtonHTMLAttributes<HTMLButtonElement>>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  margin-bottom: 8px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.gray7};
  background: ${({ theme }) => theme.colors.gray10};
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.white4};
  }
`

export interface WalletConnectSessionModalProps {
  onModalClose: () => void | Promise<void>
  /** Injected by ModalProvider (useModal) — must run to unmount the overlay; do not omit. */
  onDismiss?: () => void
}

const WalletConnectSessionModal: React.FC<WalletConnectSessionModalProps> = observer(
  ({ onModalClose, onDismiss }) => {
  const finishClose = useCallback(async () => {
    try {
      await Promise.resolve(onModalClose())
    } finally {
      onDismiss?.()
    }
  }, [onModalClose, onDismiss])
  const [useMobileBridge, setUseMobileBridge] = useState(false)
  const { t } = useTranslation()
  const theme = useTheme()
  const uri = walletStore.wcPairingUri

  useEffect(() => {
    setUseMobileBridge(preferWalletConnectMobileBridge())
  }, [])

  const mobileOptions = useMemo(() => WALLETCONNECT_MOBILE_WALLET_OPTIONS, [])

  const subtitle = useMemo(() => {
    if (!uri) return t('walletConnect.waitingForUri')
    if (useMobileBridge) return t('walletConnect.chooseWalletApp')
    return t('walletConnect.scanQrDescription')
  }, [uri, useMobileBridge, t])

  return (
    <Modal onDismiss={finishClose} style={{ maxWidth: 420, zIndex: 9999 }} title={t('walletConnect.scanToConnect')}>
      <Flex flexDirection="column" alignItems="stretch" justifyContent="center" mt="24px" px="16px" pb="16px">
        {uri && useMobileBridge ? (
          <>
            <Text variant={TextVariant.BODY_DEFAULT_BOLD} mb="12px">
              {t('walletConnect.openWalletApp')}
            </Text>
            <Text variant={TextVariant.BODY_SMALL} color={theme.colors.gray6} mb="16px">
              {t('walletConnect.mobileBridgeHint')}
            </Text>
            {mobileOptions.map((w) => (
              <WalletRow
                key={w.id}
                type="button"
                onClick={() => openWalletConnectMobileBridge(w.id, uri, mobileOptions)}
              >
                <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{w.name}</Text>
                <Text variant={TextVariant.BODY_SMALL} color={theme.colors.gray6}>
                  →
                </Text>
              </WalletRow>
            ))}
          </>
        ) : (
          <QrBlock>
            {uri ? <StyledQrCode value={uri} size={256} /> : <StyledQrCodeEmpty><StatusBar /></StyledQrCodeEmpty>}
          </QrBlock>
        )}
        <Text variant={TextVariant.BODY_DEFAULT} color={theme.colors.gray6} textAlign="center" mt="8px">
          {subtitle}
        </Text>
        <Flex width="100%" mt="24px">
          <Button variant={ButtonVariant.OUTLINED} fullWidth onClick={() => void finishClose()}>
            {t('walletConnect.cancelConnect')}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
})

export default WalletConnectSessionModal
